import type { ReturnReadiness } from '../api'

export type ReturnNotice = { key: string; title: string; message: string; details: string[] }

// Transfer messages are diagnostics: they may contain local paths or native
// errors. Only fixed presentation copy belongs in the ordinary Settings view.
export function returnNotices(readiness: ReturnReadiness | null): ReturnNotice[] {
  const notices = new Map<string, ReturnNotice>()
  const lanes = [
    ['handoff', readiness?.recent_handoffs],
    ['return', readiness?.recent_returns],
  ] as const
  for (const [lane, entries] of lanes) {
    for (const item of entries || []) {
      const status = typeof item.status === 'string' ? item.status : 'unknown'
      const detail = typeof item.message === 'string' ? item.message : ''
      if (['imported', 'downloaded', 'skipped', 'duplicate', 'submitted', 'restored'].includes(status)) continue
      if (status === 'pending' && lane === 'return' && (
        detail.startsWith('waiting for export in ') || detail === 'an export is present but is still being written'
      )) continue
      let key = `${lane}:${status}`
      let title = 'Premiere transfer needs attention'
      let message = 'Check the transfer details before trying again.'
      if (status === 'authentication_required') {
        key = 'authentication_required'
        title = 'Sign in to Motion'
        message = 'Sign in again to resume Premiere transfers.'
      } else if (status === 'pending' && detail === 'Move the previous export out of the active folder to replace it') {
        key = 'replace_export'
        title = 'Replace the previous export'
        message = 'Move the previous export out of the active return folder before returning a replacement.'
      } else if (status === 'unavailable') {
        title = lane === 'handoff' ? 'Premiere is unavailable' : 'Return storage is unavailable'
        message = lane === 'handoff'
          ? 'Open Premiere Pro and check that the Motion bridge is connected.'
          : 'Check that the return folder is connected and accessible.'
      } else if (status === 'failed') {
        title = lane === 'handoff' ? 'Premiere import could not finish' : 'Finished edit could not return'
        message = 'Check the connection and transfer details before trying again.'
      } else if (status === 'retry') {
        title = lane === 'handoff' ? 'Premiere transfer paused' : 'Return transfer paused'
        message = 'Motion will retry. Check your connection and available storage.'
      } else if (status === 'invalid') {
        title = 'Export needs attention'
        message = 'Check that the finished edit is a playable video in the expected return folder.'
      } else if (status === 'withdrawn') {
        title = 'Finished edit removed from review'
        message = 'The export was removed from its return folder. Existing media is retained.'
      }
      const diagnostic = `${lane === 'handoff' ? 'Premiere handoff' : 'Finished edit'} · ${status}${detail ? `\n${detail}` : ''}`
      const existing = notices.get(key)
      if (existing) {
        if (!existing.details.includes(diagnostic)) existing.details.push(diagnostic)
      } else notices.set(key, { key, title, message, details: [diagnostic] })
    }
  }
  return [...notices.values()]
}
